/**
 * Order reminders — WHEN the "time to place your order" email goes out.
 * SERVER SOURCE OF TRUTH. The client mirror is src/lib/orderReminders.ts and
 * must stay identical apart from its one import line
 * (tests/order-reminders.test.ts fails when they drift).
 *
 * Separate from the delivery schedule on purpose (Sky, 2026-11-02): the
 * schedule says when a store RECEIVES deliveries; this says when it is
 * REMINDED to order. Two ways to send:
 *
 *   before_delivery — the day before each delivery day, at `time`
 *                     (what every schedule did before this; needs a schedule,
 *                     and the reminder must land before the order cutoff)
 *   weekly          — on the weekdays picked in `days`, at `time`
 *                     ("every Monday at 2pm ET"); the number of days picked
 *                     is how many reminders a week
 *
 * `time` is read in `time_zone` (PT, ET, …), so "2:00 PM ET" is 2pm in New
 * York whatever the server's clock says.
 *
 * Stored as jsonb on orders.customers.order_reminders (the account's own) and
 * orders.customer_locations.order_reminders (a location override). A chain
 * store is its own account, so it carries its own row.
 */
import {
  addDays,
  dayOfWeek,
  isDeliveryDay,
  isOrderable,
  nextOrderableDeliveryDate,
  DAY_NAMES,
  type DeliverySchedule,
} from './deliverySchedule';

export type ReminderMode = 'before_delivery' | 'weekly';

export interface OrderReminders {
  enabled: boolean;
  mode: ReminderMode;
  /** Weekly mode: day indices, 0=Sunday … 6=Saturday. Stored as lowercase names. */
  days: number[];
  /** 'HH:MM' 24h in `time_zone`. */
  time: string;
  /** IANA zone, one of REMINDER_TIME_ZONES. */
  time_zone: string;
  /** Empty = the recipients ladder (EDI notify list → Primary email). */
  emails: string[];
  /** Stamped on save — nothing sends for a send time before it. */
  enabled_at: string | null;
}

export const REMINDER_TIME_ZONES = [
  { id: 'America/Los_Angeles', short: 'PT', label: 'Pacific (PT)' },
  { id: 'America/Denver', short: 'MT', label: 'Mountain (MT)' },
  { id: 'America/Phoenix', short: 'AZ', label: 'Arizona (AZ)' },
  { id: 'America/Chicago', short: 'CT', label: 'Central (CT)' },
  { id: 'America/New_York', short: 'ET', label: 'Eastern (ET)' },
  { id: 'America/Anchorage', short: 'AKT', label: 'Alaska (AKT)' },
  { id: 'Pacific/Honolulu', short: 'HT', label: 'Hawaii (HT)' },
] as const;

export const DEFAULT_REMINDER_TIME = '09:00';
export const DEFAULT_REMINDER_TZ = 'America/Los_Angeles';

const TZ_IDS = new Set<string>(REMINDER_TIME_ZONES.map((z) => z.id));
const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The defaults a brand-new schedule gets: the day before each delivery, 9am PT. */
export function defaultReminders(): OrderReminders {
  return {
    enabled: true,
    mode: 'before_delivery',
    days: [],
    time: DEFAULT_REMINDER_TIME,
    time_zone: DEFAULT_REMINDER_TZ,
    emails: [],
    enabled_at: null,
  };
}

/** Parse the order_reminders jsonb defensively. Null on absent/junk. */
export function parseOrderReminders(raw: unknown): OrderReminders | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const mode: ReminderMode = r.mode === 'weekly' ? 'weekly' : 'before_delivery';
  const days = [
    ...new Set(
      (Array.isArray(r.days) ? r.days : [])
        .map((d) =>
          typeof d === 'number' && Number.isInteger(d) && d >= 0 && d <= 6
            ? d
            : DAY_NAMES.indexOf(String(d).trim().toLowerCase() as (typeof DAY_NAMES)[number]),
        )
        .filter((d) => d >= 0),
    ),
  ].sort((a, b) => a - b);
  const time = String(r.time ?? DEFAULT_REMINDER_TIME);
  const tz = String(r.time_zone ?? DEFAULT_REMINDER_TZ);
  return {
    enabled: r.enabled === true,
    mode,
    days,
    time: /^([01]\d|2[0-3]):[0-5]\d$/.test(time) ? time : DEFAULT_REMINDER_TIME,
    time_zone: TZ_IDS.has(tz) ? tz : DEFAULT_REMINDER_TZ,
    emails: (Array.isArray(r.emails) ? r.emails : [])
      .map((e) => String(e).trim().toLowerCase())
      .filter((e) => e.includes('@')),
    enabled_at: typeof r.enabled_at === 'string' && r.enabled_at ? r.enabled_at : null,
  };
}

/**
 * Rows saved before order_reminders existed kept their reminder switch inside
 * the delivery schedule. Read them the way they always behaved: the day
 * before each delivery, from 9am PT.
 */
export function legacyReminders(schedule: DeliverySchedule | null): OrderReminders | null {
  if (!schedule) return null;
  return {
    ...defaultReminders(),
    enabled: schedule.reminder_enabled,
    emails: schedule.reminder_emails,
    enabled_at: schedule.enabled_at,
  };
}

/**
 * The reminders that apply to one location: its own override, else the
 * account's, else the legacy switch on its effective delivery schedule.
 */
export function effectiveReminders(
  locationRaw: unknown,
  customerRaw: unknown,
  schedule: DeliverySchedule | null,
): OrderReminders | null {
  return parseOrderReminders(locationRaw) ?? parseOrderReminders(customerRaw) ?? legacyReminders(schedule);
}

/* -------------------- clock in the reminder's time zone -------------------- */

export function zonedNow(
  timeZone: string,
  now: Date = new Date(),
): { date: string; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
  };
}

function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Is there a send on this local date, and if so for which delivery? */
function slotOn(
  r: OrderReminders,
  schedule: DeliverySchedule | null,
  localDate: string,
): { deliveryDate: string | null } | null {
  if (r.mode === 'weekly') {
    return r.days.includes(dayOfWeek(localDate)) ? { deliveryDate: null } : null;
  }
  if (!schedule) return null;
  const tomorrow = addDays(localDate, 1);
  return isDeliveryDay(schedule, tomorrow) ? { deliveryDate: tomorrow } : null;
}

export interface DueReminder {
  kind: ReminderMode;
  /** The dedup key in order_reminder_log.delivery_date: the delivery date for
   *  before_delivery (same key as before this change), the send date for weekly. */
  slot_date: string;
  /** The delivery the email talks about. Null when weekly with no schedule. */
  delivery_date: string | null;
}

/**
 * Is a reminder due right now? True from the chosen time until the end of
 * that day (the cron runs every 30 minutes and the log claim makes it once),
 * and — for before_delivery — only while that delivery can still be ordered.
 */
export function reminderDueNow(
  r: OrderReminders,
  schedule: DeliverySchedule | null,
  now: Date = new Date(),
): DueReminder | null {
  if (!r.enabled) return null;
  const local = zonedNow(r.time_zone, now);
  if (local.hour * 60 + local.minute < minutesOf(r.time)) return null;
  const slot = slotOn(r, schedule, local.date);
  if (!slot) return null;

  if (!savedBeforeSend(r, local.date)) return null;

  if (r.mode === 'before_delivery') {
    if (!schedule || !slot.deliveryDate || !isOrderable(schedule, slot.deliveryDate, now)) return null;
    return { kind: r.mode, slot_date: slot.deliveryDate, delivery_date: slot.deliveryDate };
  }
  return {
    kind: r.mode,
    slot_date: local.date,
    delivery_date: schedule ? nextOrderableDeliveryDate(schedule, now) : null,
  };
}

/**
 * No-backfill guard: a reminder saved at 3pm for a 2pm send must not fire at
 * 3:30 the same day. Saved on an earlier day, or earlier the same day than
 * the send time → fine. Rows with no stamp (legacy SQL) are not guarded.
 */
function savedBeforeSend(r: OrderReminders, localDate: string): boolean {
  if (!r.enabled_at) return true;
  const ms = Date.parse(r.enabled_at);
  if (!Number.isFinite(ms)) return true;
  const saved = zonedNow(r.time_zone, new Date(ms));
  if (saved.date < localDate) return true;
  if (saved.date > localDate) return false;
  return saved.hour * 60 + saved.minute <= minutesOf(r.time);
}

/** The next send (today's still counts if its time hasn't passed). */
export function nextReminderSend(
  r: OrderReminders,
  schedule: DeliverySchedule | null,
  now: Date = new Date(),
): { send_date: string; delivery_date: string | null } | null {
  if (!r.enabled) return null;
  const local = zonedNow(r.time_zone, now);
  const pastToday = local.hour * 60 + local.minute >= minutesOf(r.time);
  for (let i = pastToday ? 1 : 0; i <= 35; i += 1) {
    const d = addDays(local.date, i);
    const slot = slotOn(r, schedule, d);
    if (!slot) continue;
    if (r.mode === 'before_delivery') return { send_date: d, delivery_date: slot.deliveryDate };
    return { send_date: d, delivery_date: schedule ? firstDeliveryAfter(schedule, d) : null };
  }
  return null;
}

/** First delivery day strictly after a date (a weekly reminder's "your next delivery"). */
function firstDeliveryAfter(s: DeliverySchedule, dateStr: string): string | null {
  for (let i = 1; i <= 35; i += 1) {
    const d = addDays(dateStr, i);
    if (isDeliveryDay(s, d)) return d;
  }
  return null;
}

/** A wall-clock date + time in a zone → the UTC instant (DST-safe). */
export function zonedToUtc(dateStr: string, hhmm: string, timeZone: string): Date {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = hhmm.split(':').map(Number);
  const wall = Date.UTC(y ?? 1970, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  const offsetAt = (t: number) => {
    const z = zonedNow(timeZone, new Date(t));
    const [zy, zm, zd] = z.date.split('-').map(Number);
    return Date.UTC(zy ?? 1970, (zm ?? 1) - 1, zd ?? 1, z.hour, z.minute) - t;
  };
  let t = wall - offsetAt(wall);
  t = wall - offsetAt(t);
  return new Date(t);
}

/**
 * Weekly reminders: when the PREVIOUS reminder went out — the last picked day
 * before today, at the reminder's time. Thursday + Friday at 9am → on Friday
 * this is Thursday 9am; Mondays only → last Monday.
 */
export function previousWeeklySend(r: OrderReminders, now: Date = new Date()): Date | null {
  if (r.mode !== 'weekly' || r.days.length === 0) return null;
  const local = zonedNow(r.time_zone, now);
  for (let i = 1; i <= 7; i += 1) {
    const d = addDays(local.date, -i);
    if (r.days.includes(dayOfWeek(d))) return zonedToUtc(d, r.time, r.time_zone);
  }
  return null;
}

/**
 * Has this reminder already been answered? (Sky, 2026-11-03: "if he puts an
 * order in late thursday, will the [Friday] reminder still go off … shouldn't
 * the system check first?" and "check to insure it has an order in the
 * future at all".) The PostgREST `or=(…)` body for the orders query — the
 * reminder is skipped when the location has ANY of:
 *
 *   1. an OPEN order: not delivered yet (delivered_at is stamped when Service
 *      Fusion invoices it, normally the same evening), placed in the last 30
 *      days, and dated today or later — or not dated at all;
 *   2. an order for the delivery the email is about;
 *   3. weekly: any order placed since the previous reminder went out
 *      (Thursday 9am → Friday 9am), even one already delivered;
 *      day-before: a dateless order placed in the last 20 hours.
 *
 * The caller adds status != cancelled.
 */
export function alreadyOrderedFilter(
  r: OrderReminders,
  deliveryDate: string | null,
  now: Date = new Date(),
): string {
  const today = zonedNow(r.time_zone, now).date;
  const openSince = new Date(now.getTime() - 30 * 86_400_000).toISOString();
  const parts: string[] = [
    `and(delivered_at.is.null,created_at.gte.${openSince},or(requested_delivery_date.gte.${today},sf_job_scheduled_date.gte.${today},and(requested_delivery_date.is.null,sf_job_scheduled_date.is.null)))`,
  ];
  if (deliveryDate) parts.push(`requested_delivery_date.eq.${deliveryDate}`);
  if (r.mode === 'weekly') {
    const since = previousWeeklySend(r, now) ?? new Date(now.getTime() - 7 * 86_400_000);
    parts.push(`created_at.gte.${since.toISOString()}`);
  } else {
    const since = new Date(now.getTime() - 20 * 60 * 60 * 1000);
    parts.push(`and(requested_delivery_date.is.null,created_at.gte.${since.toISOString()})`);
  }
  return parts.join(',');
}

/* -------------------- display -------------------- */

export function tzShort(timeZone: string): string {
  return REMINDER_TIME_ZONES.find((z) => z.id === timeZone)?.short ?? timeZone;
}

/** "2:00 PM" from '14:00'. */
export function fmtReminderTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const hour = ((h ?? 0) + 11) % 12 + 1;
  return `${hour}:${String(m ?? 0).padStart(2, '0')} ${(h ?? 0) >= 12 ? 'PM' : 'AM'}`;
}

/** "Mondays & Thursdays · 2:00 PM ET" / "Day before each delivery · 9:00 AM PT" / "Off". */
export function describeReminders(r: OrderReminders | null): string {
  if (!r || !r.enabled) return 'Off';
  const at = `${fmtReminderTime(r.time)} ${tzShort(r.time_zone)}`;
  if (r.mode === 'before_delivery') return `Day before each delivery · ${at}`;
  if (r.days.length === 0) return 'No days picked';
  const names = r.days.map((d) => `${DAY_LABELS[d]}s`);
  const list =
    names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
  return `${list} · ${at}`;
}
